import './Login.css'
import { FormEvent, ReactNode, useRef, useState } from 'react'
import { Form, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { authService } from "../authService";
import { useAuth } from "../AuthContext";
import { useAccess } from "../access/PlatformAccess";

interface LoginLocationState {
    from?: { pathname?: string };
}

function Login(): ReactNode {
    const { t } = useTranslation(['login', 'base']);
    const { refreshUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [loginValue, setLoginValue] = useState<string>("");
    const [passwordValue, setPasswordValue] = useState<string>("");
    const [errorInfo, setErrorInfo] = useState<ReactNode>(<div></div>);
    const [isLoading, setIsLoading] = useState(false);

    // disabled на кнопке снимает только следующий рендер, поэтому от двойного
    // клика страхуемся ref-ом: второй submit не должен уйти на сервер.
    const isSubmittingRef = useRef(false);

    const access = useAccess();

    async function submitForm(event: FormEvent<HTMLFormElement>): Promise<void> {
        event.preventDefault();

        if (isSubmittingRef.current) {
            return;
        }

        isSubmittingRef.current = true;
        setErrorInfo(<div></div>);
        setIsLoading(true);

        try {
            // Ответ 204 без тела: сессия уже в cookie, данные о пользователе
            // забираем отдельным запросом /api/auth/me.
            await authService.login(loginValue, passwordValue);
            await refreshUser();

            const from = (location.state as LoginLocationState | null)?.from?.pathname;
            navigate(from ?? "/", { replace: true });
        } catch (error) {
            const message = (error as AxiosError)?.response?.status === 502
                ? t('auth_unavailable')
                : t('login_incorrect');

            setErrorInfo(<div style={{ color: "red", paddingTop: "10px" }}>{message}</div>);
        } finally {
            isSubmittingRef.current = false;
            setIsLoading(false);
        }
    }

    return (
        <div className='form-page'>
            <div className='form-page-container'>
                <Form onSubmit={submitForm}>
                    <Form.Group className="login-form" controlId="formBasicEmail">
                        <Form.Label>{t('address')}</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder={t('address_placeholder')}
                            value={loginValue}
                            onChange={e => setLoginValue(e.currentTarget.value)}
                        />
                    </Form.Group>

                    <Form.Group className="login-form" controlId="formBasicPassword">
                        <Form.Label>{t('password')}</Form.Label>
                        <Form.Control
                            type="password"
                            placeholder={t('password_placeholder')}
                            value={passwordValue}
                            onChange={e => setPasswordValue(e.currentTarget.value)}
                        />
                    </Form.Group>
                    <div className='login-buttons'>
                        <Button variant="primary" type="submit" disabled={isLoading}>
                            {t('Enter', { ns: "base" })}
                        </Button>
                        <Button variant="light" href='/registration' disabled={isLoading}>
                            {t('Register', { ns: "base" })}
                        </Button>
                    </div>
                </Form>
                <div style={{ marginTop: "10px" }}>
                    <a className='forgot-pass t-14' href='/forgot-pass'>{t('forgot_pass') + " "}</a>
                    {access.LoginLinks && <access.LoginLinks />}
                </div>
                {errorInfo}
            </div>
        </div>
    )
}

export default Login;